// Outbound cross-domain SSO hand-off, spoke side. Contract (hub repo):
// docs/engineering/sso-hand-off-spoke-contract.md. A visitor signed in on the hub (trackingmcp.com)
// is signed in here without a second login: on the first signed-out load we send the tab to
// `https://trackingmcp.com/auth/hand-off?return=<this page>`; the hub comes back with `#sso=<nonce>`,
// which adopt-session.ts redeems once. This file is only the DECISION and the redirect.
//
// Hard rules (all pinned by sso-hand-off.test.ts):
//  - ONE attempt per tab session: the `sso_tried` marker is written BEFORE the redirect, and any
//    marker (or a marker we cannot read) means "never redirect again".
//  - The return URL is path + query only. No fragment, no token, no nonce ever rides a URL we build.
//  - Only the auth-needing routes trigger it. Marketing pages stay as they are for people and
//    crawlers: an anonymous visitor must never be bounced to a hub login page just for reading.
//  - Never for crawlers/automation, offline, framed (embedded) pages, or a non-production host
//    (the hub allowlist only knows the family origins, so localhost/staging would be refused anyway).

export const HUB_HAND_OFF_URL = 'https://trackingmcp.com/auth/hand-off'
export const SSO_TRIED_KEY = 'sso_tried'

// Routes that need a session and may start a hand-off. Everything else is public and never redirects.
export const HAND_OFF_ROUTES: readonly string[] = ['/login', '/dashboard']

// Exact family spoke hosts the hub accepts as `return` (https only, no port). Keep in step with the
// hub's FAMILY_APEX_HOSTS.
const FAMILY_HOSTS: ReadonlySet<string> = new Set(
  ['aircargomcp.com', 'airratesmcp.com'].flatMap((h) => [h, `www.${h}`]),
)

const BOT_UA =
  /bot|crawl|spider|slurp|bing|google|yandex|baidu|duckduck|facebookexternalhit|embedly|preview|lighthouse|headless|phantom|puppeteer|playwright|ahrefs|semrush|curl|wget|python|node-fetch|axios/i

// Query params that must never be copied into a URL we send to another origin.
const SENSITIVE_PARAMS = ['access_token', 'refresh_token', 'token', 'sso', 'code']

export type HandOffInput = {
  session: boolean
  loading: boolean
  /** location.hash */
  hash: string
  /** value of sessionStorage.sso_tried; 'unreadable' when storage threw */
  marker: string | null | 'unreadable'
  protocol: string
  hostname: string
  port: string
  pathname: string
  search: string
  userAgent: string
  webdriver: boolean
  online: boolean
  framed: boolean
}

export type HandOffDecision =
  | { action: 'redirect'; url: string }
  | { action: 'skip'; reason: string }

/** The `return` value for this page: https origin + path + query, minus sensitive params, no hash. */
export function buildReturnUrl(i: Pick<HandOffInput, 'hostname' | 'pathname' | 'search'>): string {
  const params = new URLSearchParams(i.search)
  for (const k of SENSITIVE_PARAMS) params.delete(k)
  const qs = params.toString()
  return `https://${i.hostname}${i.pathname}${qs ? `?${qs}` : ''}`
}

export function buildHandOffUrl(i: Pick<HandOffInput, 'hostname' | 'pathname' | 'search'>): string {
  return `${HUB_HAND_OFF_URL}?return=${encodeURIComponent(buildReturnUrl(i))}`
}

const hasNonce = (hash: string) => /(^#|&)sso=/.test(hash)

/** Pure: should this load start a hand-off? Never throws. */
export function decideHandOff(i: HandOffInput): HandOffDecision {
  const skip = (reason: string): HandOffDecision => ({ action: 'skip', reason })
  if (i.loading) return skip('loading')
  if (i.session) return skip('has-session')
  if (hasNonce(i.hash)) return skip('nonce-in-url')
  if (i.marker !== null) return skip('already-tried')
  if (i.framed) return skip('embedded')
  if (!i.online) return skip('offline')
  if (i.webdriver || BOT_UA.test(i.userAgent) || !i.userAgent) return skip('bot')
  if (i.protocol !== 'https:' || i.port !== '') return skip('not-production-origin')
  if (!FAMILY_HOSTS.has(i.hostname)) return skip('not-family-host')
  const path = i.pathname.replace(/\/+$/, '') || '/'
  if (!HAND_OFF_ROUTES.includes(path)) return skip('public-route')
  return { action: 'redirect', url: buildHandOffUrl(i) }
}

function readInput(session: boolean, loading: boolean): HandOffInput {
  let marker: HandOffInput['marker']
  try {
    marker = window.sessionStorage.getItem(SSO_TRIED_KEY)
  } catch {
    marker = 'unreadable'
  }
  const l = window.location
  let framed = false
  try {
    framed = window.self !== window.top
  } catch {
    framed = true
  }
  return {
    session,
    loading,
    hash: l.hash,
    marker,
    protocol: l.protocol,
    hostname: l.hostname,
    port: l.port,
    pathname: l.pathname,
    search: l.search,
    userAgent: navigator.userAgent ?? '',
    webdriver: Boolean(navigator.webdriver),
    online: navigator.onLine !== false,
    framed,
  }
}

/**
 * Call once the session state is settled (after adoptSessionFromUrl + getSession). Writes the marker
 * FIRST; if it cannot be written, does not redirect (a loop is worse than a second login).
 */
export function startHandOffIfNeeded(session: boolean, loading: boolean): boolean {
  if (typeof window === 'undefined') return false
  const decision = decideHandOff(readInput(session, loading))
  if (decision.action !== 'redirect') return false
  try {
    window.sessionStorage.setItem(SSO_TRIED_KEY, '1')
    if (window.sessionStorage.getItem(SSO_TRIED_KEY) !== '1') return false
  } catch {
    return false
  }
  window.location.replace(decision.url)
  return true
}

/**
 * Mark the attempt as made without redirecting. Used when this load arrives WITH a nonce, so a failed
 * redemption cannot lead to a second hand-off in the same tab session.
 */
export function markHandOffTried(): void {
  try {
    window.sessionStorage.setItem(SSO_TRIED_KEY, '1')
  } catch {
    /* unreadable storage already means "do not redirect" */
  }
}
